import React, { useState, useEffect } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis } from 'recharts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '../ui/chart';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { sesionesService } from '../../services/sesionesService';

const chartConfig = {
  sessions: {
    label: 'Sesiones Totales',
    color: 'hsl(var(--chart-1))',
  },
  sessionsWithSimulation: {
    label: 'Sesiones con Simulación',
    color: 'hsl(var(--chart-2))',
  },
  quotedPlans: {
    label: 'Planes Cotizados',
    color: 'hsl(var(--chart-3))',
  },
};

const SessionsAreaChart = () => {
  const [timeRange, setTimeRange] = useState('30d');
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const timeRangeOptions = {
    '7d': { days: 7, label: '7 días' },
    '30d': { days: 30, label: '30 días' },
    '90d': { days: 90, label: '90 días' },
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const days = timeRangeOptions[timeRange].days;
        
        // Obtener datos de sesiones totales
        const totalSessionsData = await sesionesService.getSessionsByDay(days);
        
        // Obtener datos de sesiones con simulación y planes cotizados
        const simulationData = await sesionesService.getSessionsWithSimulationByDay(days);
        const quotedPlansData = await sesionesService.getQuotedPlansByDay(days);
        
        // Combinar todos los datos
        const formattedData = totalSessionsData.map(item => {
          const simulationItem = simulationData.find(s => s.date === item.date) || { sessions: 0 };
          const quotedItem = quotedPlansData.find(q => q.date === item.date) || { sessions: 0 };
          
          return {
            date: new Date(item.date).toLocaleDateString('es-ES', {
              month: 'short',
              day: 'numeric'
            }),
            sessions: item.sessions,
            sessionsWithSimulation: simulationItem.sessions,
            quotedPlans: quotedItem.sessions,
          };
        });
        
        setChartData(formattedData);
      } catch (err) {
        console.error('Error fetching sessions data:', err);
        setError('Error al cargar los datos de sesiones');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange]);

  const totalSessions = chartData.reduce((acc, curr) => acc + curr.sessions, 0);
  const totalSimulations = chartData.reduce((acc, curr) => acc + curr.sessionsWithSimulation, 0);
  const totalQuotedPlans = chartData.reduce((acc, curr) => acc + curr.quotedPlans, 0);
  const avgSessions = chartData.length > 0 ? (totalSessions / chartData.length).toFixed(1) : 0;
  const avgSimulations = chartData.length > 0 ? (totalSimulations / chartData.length).toFixed(1) : 0;
  const avgQuotedPlans = chartData.length > 0 ? (totalQuotedPlans / chartData.length).toFixed(1) : 0;

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Sesiones por Día</CardTitle>
          <CardDescription>Error al cargar los datos</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center text-red-500 py-8">
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex items-center gap-2 space-y-0 border-b py-5 sm:flex-row">
        <div className="grid flex-1 gap-1 text-center sm:text-left">
          <CardTitle>Análisis de Sesiones por Día</CardTitle>
          <CardDescription>
            Sesiones totales, con simulación y planes cotizados para los últimos {timeRangeOptions[timeRange].label.toLowerCase()}
          </CardDescription>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger
            className="w-[160px] rounded-lg sm:ml-auto"
            aria-label="Seleccionar período"
          >
            <SelectValue placeholder="Seleccionar período" />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            {Object.entries(timeRangeOptions).map(([key, option]) => (
              <SelectItem key={key} value={key} className="rounded-lg">
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        {loading ? (
          <div className="flex items-center justify-center h-[250px]">
            <div className="text-muted-foreground">Cargando datos...</div>
          </div>
        ) : (
          <>
            <div className="mb-4 grid grid-cols-3 gap-4 text-center">
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Sesiones Totales</div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="grid gap-1">
                    <div className="text-xl font-bold">{totalSessions}</div>
                    <div className="text-xs text-muted-foreground">Total</div>
                  </div>
                  <div className="grid gap-1">
                    <div className="text-xl font-bold">{avgSessions}</div>
                    <div className="text-xs text-muted-foreground">Promedio</div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Con Simulación</div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="grid gap-1">
                    <div className="text-xl font-bold">{totalSimulations}</div>
                    <div className="text-xs text-muted-foreground">Total</div>
                  </div>
                  <div className="grid gap-1">
                    <div className="text-xl font-bold">{avgSimulations}</div>
                    <div className="text-xs text-muted-foreground">Promedio</div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Planes Cotizados</div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="grid gap-1">
                    <div className="text-xl font-bold">{totalQuotedPlans}</div>
                    <div className="text-xs text-muted-foreground">Total</div>
                  </div>
                  <div className="grid gap-1">
                    <div className="text-xl font-bold">{avgQuotedPlans}</div>
                    <div className="text-xs text-muted-foreground">Promedio</div>
                  </div>
                </div>
              </div>
            </div>
            <ChartContainer
              config={chartConfig}
              className="aspect-auto h-[250px] w-full"
            >
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="fillSessions" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor="var(--color-sessions)"
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="95%"
                      stopColor="var(--color-sessions)"
                      stopOpacity={0.1}
                    />
                  </linearGradient>
                  <linearGradient id="fillSessionsWithSimulation" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor="var(--color-sessionsWithSimulation)"
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="95%"
                      stopColor="var(--color-sessionsWithSimulation)"
                      stopOpacity={0.1}
                    />
                  </linearGradient>
                  <linearGradient id="fillQuotedPlans" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor="var(--color-quotedPlans)"
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="95%"
                      stopColor="var(--color-quotedPlans)"
                      stopOpacity={0.1}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={32}
                  tickFormatter={(value) => value}
                />
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      labelFormatter={(value) => `Fecha: ${value}`}
                      indicator="dot"
                    />
                  }
                />
                <Area
                  dataKey="sessions"
                  type="natural"
                  fill="url(#fillSessions)"
                  stroke="var(--color-sessions)"
                  stackId="a"
                />
                <Area
                  dataKey="sessionsWithSimulation"
                  type="natural"
                  fill="url(#fillSessionsWithSimulation)"
                  stroke="var(--color-sessionsWithSimulation)"
                  stackId="b"
                />
                <Area
                  dataKey="quotedPlans"
                  type="natural"
                  fill="url(#fillQuotedPlans)"
                  stroke="var(--color-quotedPlans)"
                  stackId="c"
                />
                <ChartLegend content={<ChartLegendContent />} />
              </AreaChart>
            </ChartContainer>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SessionsAreaChart;